import {
    CursorArrowRaysIcon,
    EnvelopeIcon,
    EnvelopeOpenIcon,
    PaperClipIcon,
} from "@heroicons/react/24/outline";
import { ago, SentMessageId, SentMessageType, sentMessageTypeValues } from "@sp-crm/core";
import { ClientPersonaLink } from "components/clients/list-client/client-persona-link";
import { ConversationFeedBasicsFragment } from "generated/graphql";
import React from "react";
import { Link } from "react-router-dom";
import { useFeature } from "store/selectors/hooks";
import {
    BouncedStatusMessage,
    DeliveredStatusMessage,
    NotDeliveredStatusMessage,
    SendingStatusMessage,
    SentStatusMessage,
} from "./status-message";

interface SentMessageStatusMessageProps {
    status: string;
    latestEventStatus: string | null | undefined;
    messageType: SentMessageType;
}
const SentMessageStatusMessage: React.FC<SentMessageStatusMessageProps> = props => {
    const { status, latestEventStatus, messageType } = props;

    if (status === "warning") {
        return <BouncedStatusMessage mode="icon-and-text" />;
    }
    if (status === "error") {
        return <NotDeliveredStatusMessage mode="icon-and-text" />;
    }
    if (status === "success") {
        return <DeliveredStatusMessage mode="icon-and-text" />;
    }
    if (
        latestEventStatus === "fax-send-failure" ||
        latestEventStatus === "fax-delivery-failure"
    ) {
        return <NotDeliveredStatusMessage mode="icon-and-text" />;
    }
    if (latestEventStatus === "fax-delivery-success") {
        return <DeliveredStatusMessage mode="icon-and-text" />;
    }
    if (!latestEventStatus && messageType === "ClientIntakeFax") {
        return <SendingStatusMessage mode="icon-and-text" />;
    }

    if (latestEventStatus === "email-send-success") {
        return <SentStatusMessage mode="icon-and-text" />;
    }

    return null;
};

const humanReadableWellFormedMessageType = (input: SentMessageType): string => {
    const mapping: Record<SentMessageType, string> = {
        ClientIntake: "Client Referral",
        ClientIntakeFax: "Client Referral (fax)",
        CommunityComparison: "Community Comparison",
        Email: "",
        Invoice: "Invoice",
        DisclosureRequest: "Disclosure Request",
        DisclosureReceipt: "Disclosure Receipt",
        HostedFormRequest: "Share Input Form",
        TestOnlyMessage: "",
        SignatureCompletion: "Signature Completion",
        SignatureRequest: "Signature Request",
        BulkMessage: "Outreach",
        HostedFormReferralLink: "Share Input Form w/ Referral",
        AvailabilityRequest: "Availability Request",
    };
    return mapping[input] || "";
};

const humanReadableMessageType = (input: string | null | undefined) => {
    if (sentMessageTypeValues.includes(input as SentMessageType)) {
        return humanReadableWellFormedMessageType(input as SentMessageType);
    }
    return "";
};

interface SentMessageThreadProps {
    thread: ConversationFeedBasicsFragment["threads"][number];
    onView: (id: SentMessageId) => void;
    showClient: boolean;
    showCommunity: boolean;
}

export const SentMessageThread: React.FC<SentMessageThreadProps> = props => {
    const { thread, onView } = props;

    const enhancedMode = useFeature("enhancedSentMessageView");

    const m = thread.sentMessage;

    const showClient = props.showClient && !!m.intakeClient;
    const showCommunity = props.showCommunity && !!m.intakeCommunity;

    const openedEvents = m.events ? m.events.filter(e => e.eventType === "opened") : [];
    const attachments = m.attachments || [];
    const clickCount =
        m.clickedLinkEvents?.reduce((acc, e) => acc + (e.eventsV2?.length || 0), 0) || 0;
    const messageTypeLabel = humanReadableMessageType(m.messageType);

    return (
        <li key={m.id} className="mb-4">
            <div
                className="bg-white rounded-lg shadow p-4 hover:bg-brand-50 cursor-pointer transition-colors"
                onClick={() => onView(m.id)}>
                <div className="flex">
                    <div className="flex-shrink-0 mr-4">
                        <EnvelopeIcon
                            className="h-5 w-5 text-gray-500 mt-2"
                            aria-hidden="true"
                        />
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="flex flex-col md:flex-row md:items-start md:justify-between">
                            <div className="flex flex-col space-y-1 min-w-0 flex-1">
                                <div className="flex flex-col md:flex-row md:items-center gap-2 min-w-0">
                                    <div className="font-medium text-gray-900 break-words">
                                        {m.subject}
                                    </div>
                                    {messageTypeLabel ? (
                                        <div className="flex-shrink-0">
                                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full bg-gray-100 text-sm text-gray-500">
                                                {messageTypeLabel}
                                            </span>
                                        </div>
                                    ) : null}
                                </div>
                                {showCommunity && m.intakeCommunity ? (
                                    <div className="flex flex-wrap gap-2 text-sm text-gray-500">
                                        <Link
                                            className="hover:text-gray-700"
                                            to={m.intakeCommunity.appLink}>
                                            {m.intakeCommunity.name ?? "(no name)"}
                                        </Link>
                                    </div>
                                ) : null}
                                <div className="text-sm text-gray-500 break-words">
                                    {(m.to ?? []).join(", ")}
                                </div>
                                {showClient && m.intakeClient ? (
                                    <div className="flex-shrink-0 pt-2">
                                        <ClientPersonaLink client={m.intakeClient} />
                                    </div>
                                ) : null}
                            </div>

                            <div className="text-sm text-gray-500 flex-shrink-0 md:ml-4 mt-2 md:mt-0">
                                {ago(m.createdAt, "")}
                            </div>
                        </div>

                        <div className="mt-2 flex flex-col md:flex-row md:flex-wrap gap-2 text-sm text-gray-500">
                            <div className="flex gap-2">
                                <SentMessageStatusMessage
                                    messageType={m.messageType as SentMessageType}
                                    status={m.status}
                                    latestEventStatus={m.latestEventStatus}
                                />
                            </div>

                            <div className="mt-2 md:mt-0 md:ml-auto flex gap-2">
                                {attachments.length > 0 ? (
                                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-gray-100">
                                        <PaperClipIcon className="h-4 w-4" />
                                        {attachments.length}
                                    </span>
                                ) : null}

                                {enhancedMode ? (
                                    <>
                                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-gray-100">
                                            <EnvelopeOpenIcon className="h-4 w-4" />
                                            {openedEvents.length}
                                        </span>
                                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-gray-100">
                                            <CursorArrowRaysIcon className="h-4 w-4" />
                                            {clickCount}
                                        </span>
                                    </>
                                ) : null}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </li>
    );
};
